import { logger } from "../utils/logger";
import { secureStorage } from '../utils/secureStorage';
import { normalizeRedirectQueryValue } from '../../shared/authRedirectPolicy';
import { normalizePostLoginRedirectTarget } from '../utils/subdomainUtils';
/**
 * Centralized Redirect Service for Enterprise-Grade Authentication
 *
 * Handles all redirect logic for authentication flows with proper
 * security considerations and fallback mechanisms.
 */
// import { logger } from "../utils/logger";
export interface RedirectOptions {
    /** Whether to clear the stored redirect URL after retrieval */
    clearAfterRetrieval?: boolean;
    /** Default redirect URL if none is stored */
    defaultUrl?: string;
    /** URLs that should never be stored as redirect destinations */
    excludedUrls?: string[];
}
class RedirectService {
    private static readonly STORAGE_KEY = 'auth_redirect_url';
    private static readonly DEFAULT_EXCLUDED_URLS = [
        '/login',
        '/signup',
        '/signup-success',
        '/forgot-password',
        '/register',
        '/auth/login',
        '/provider-signup',
        '/employer-signup',
        '/professional-signup',
        '/logout',
        '/backoffice',
        '/training-manager-event-create',
        '/coordinator-event-create',
        '/dashboard/training-manager-event-create',
        '/dashboard/coordinator-event-create'
    ];
    /**
     * Store a redirect URL for post-authentication navigation
     * @param url - The URL to redirect to after authentication
     * @param options - Configuration options
     */
    public static async storeRedirectUrl(url: string, options: RedirectOptions = {}): Promise<void> {
        const { excludedUrls = this.DEFAULT_EXCLUDED_URLS } = options;
        const base =
            typeof window !== 'undefined' ? window.location.href : undefined;
        const normalized = normalizeRedirectQueryValue(url, base);
        if (!normalized) {
            logger.debug(`[RedirectService] ❌ Not storing invalid or auth redirect URL: ${url}`);
            return;
        }
        // Don't store excluded URLs
        if (excludedUrls.some(excluded => normalized.startsWith(excluded))) {
            logger.debug(`[RedirectService] ❌ Not storing excluded URL: ${normalized}`);
            return;
        }
        try {
            // Use sessionStorage for better security (cleared on tab close)
            sessionStorage.setItem(this.STORAGE_KEY, normalized);
            // Also store in secureStorage as encrypted backup for cross-tab scenarios
            await secureStorage.setEncrypted(this.STORAGE_KEY, normalized);
            logger.debug(`[RedirectService] ✅ STORED redirect URL: "${normalized}"`);
            logger.security(`[RedirectService] Verify sessionStorage: "${sessionStorage.getItem(this.STORAGE_KEY)}"`);
            const encryptedUrl = await secureStorage.getEncrypted(this.STORAGE_KEY);
            logger.security(`[RedirectService] Verify secureStorage: "${encryptedUrl}"`);
        }
        catch (error) {
            logger.error('[RedirectService] Failed to store redirect URL:', error, {});
        }
    }
    /**
     * Retrieve the stored redirect URL
     * @param options - Configuration options
     * @returns The stored redirect URL or null if none exists
     */
    public static async getRedirectUrl(options: RedirectOptions = {}): Promise<string | null> {
        const { clearAfterRetrieval = true, defaultUrl = '/dashboard', excludedUrls = this.DEFAULT_EXCLUDED_URLS } = options;
        try {
            // Try sessionStorage first (more secure)
            let redirectUrl = sessionStorage.getItem(this.STORAGE_KEY);
            // Fallback to secureStorage (encrypted)
            if (!redirectUrl) {
                redirectUrl = await secureStorage.getEncrypted(this.STORAGE_KEY);
            }
            // Remove legacy localStorage fallback (no longer used)
            // Validate the URL
            if (redirectUrl && !excludedUrls.some(excluded => redirectUrl!.startsWith(excluded))) {
                if (clearAfterRetrieval) {
                    this.clearRedirectUrl();
                }
                logger.debug(`[RedirectService] Retrieved redirect URL: ${redirectUrl}`);
                return normalizePostLoginRedirectTarget(redirectUrl);
            }
            // Return default if no valid URL found
            return defaultUrl;
        }
        catch (error) {
            logger.error('[RedirectService] Failed to retrieve redirect URL:', error, {});
            return defaultUrl;
        }
    }
    /**
     * Clear the stored redirect URL
     */
    public static async clearRedirectUrl(): Promise<void> {
        try {
            sessionStorage.removeItem(this.STORAGE_KEY);
            await secureStorage.remove(this.STORAGE_KEY);
            logger.debug('[RedirectService] Cleared redirect URL');
        }
        catch (error) {
            logger.error('[RedirectService] Failed to clear redirect URL:', error, {});
        }
    }
    /**
     * Check if a redirect URL is currently stored
     * @returns True if a redirect URL is stored
     */
    public static async hasRedirectUrl(): Promise<boolean> {
        try {
            const sessionUrl = sessionStorage.getItem(this.STORAGE_KEY);
            const secureUrl = await secureStorage.getEncrypted(this.STORAGE_KEY);
            return !!(sessionUrl || secureUrl);
        }
        catch (error) {
            logger.error('[RedirectService] Failed to check redirect URL:', error, {});
            return false;
        }
    }
    /**
     * Consume (retrieve and clear) the stored redirect URL in one atomic operation
     * @param options - Configuration options
     * @returns The stored redirect URL or default URL
     */
    public static consumeRedirect(options: RedirectOptions = {}): string {
        const { defaultUrl = '/dashboard', excludedUrls = this.DEFAULT_EXCLUDED_URLS } = options;
        logger.debug(`[RedirectService] 🔍 CONSUMING redirect URL...`);
        logger.debug(`[RedirectService] Default URL: "${defaultUrl}"`);
        logger.debug(`[RedirectService] Excluded URLs:`, { "arg0": excludedUrls });
        try {
            // Try sessionStorage first (more secure)
            let redirectUrl = sessionStorage.getItem(this.STORAGE_KEY);
            logger.debug(`[RedirectService] sessionStorage value: "${redirectUrl}"`);
            // Fallback to localStorage
            if (!redirectUrl) {
                redirectUrl = localStorage.getItem(this.STORAGE_KEY);
                logger.debug(`[RedirectService] localStorage value: "${redirectUrl}"`);
            }
            // Validate the URL
            if (redirectUrl && !excludedUrls.some(excluded => redirectUrl!.startsWith(excluded))) {
                // Clear the URL immediately after retrieval
                this.clearRedirectUrl();
                logger.debug(`[RedirectService] ✅ CONSUMED redirect URL: "${redirectUrl}"`);
                return normalizePostLoginRedirectTarget(redirectUrl);
            }
            else if (redirectUrl) {
                logger.debug(`[RedirectService] ❌ Redirect URL "${redirectUrl}" is excluded, using default`);
            }
            // Return default if no valid URL found
            logger.debug(`[RedirectService] ⚠️ No valid redirect URL found, using default: "${defaultUrl}"`);
            return defaultUrl;
        }
        catch (error) {
            logger.error('[RedirectService] Failed to consume redirect URL:', error, {});
            return defaultUrl;
        }
    }
    /**
     * Store current page URL as redirect destination
     * @param currentPath - Current pathname + search + hash
     * @param options - Configuration options
     */
    public static async storeCurrentUrlAsRedirect(currentPath: string, options: RedirectOptions = {}): Promise<void> {
        await this.storeRedirectUrl(currentPath, options);
    }
}
export default RedirectService;
